import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, XCircle, RotateCcw, Loader2, Info } from "lucide-react";
import { SuiteLayout, Glass, GhostButton } from "../../components/suite/SuiteLayout";
import { CreditsHud } from "../../components/suite/CreditsHud";
import { PricingCards, useStartTrial } from "../../components/suite/PricingCards";
import { AddonBanner, useBuyAddon } from "../../components/suite/HomeSections";
import { Plans, usePlan, errText, daysLeft } from "../../lib/plans";
import { SuiteConfirm } from "../../components/suite/BatchConfirm";
import { PlanChanger } from "../../components/suite/PlanChanger";

const STATUS = { trialing: "Free trial", active: "Active", past_due: "Payment issue", canceled: "Canceled", none: "No plan" };

export default function SuiteBilling() {
  const { plan, set } = usePlan();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(null);
  const [askCancel, setAskCancel] = useState(false);
  const { start } = useStartTrial();
  const { buy } = useBuyAddon();
  const auto = useRef(false);

  // Resume a "Try …" / add-on intent that started before sign-in.
  useEffect(() => {
    if (!plan || auto.current) return;
    const tier = params.get("try");
    if (tier && !["trialing", "active"].includes(plan.status)) { auto.current = true; start(tier); }
    else if (params.get("addon")) { auto.current = true; buy(); }
    if (tier || params.get("addon")) setParams({}, { replace: true });
  }, [plan]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, fn, ok) => {
    setBusy(id);
    try { const r = await fn(); if (r?.url) return window.location.assign(r.url); set(r); if (ok) toast.success(ok); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(null); }
  };

  const has = plan && ["trialing", "active", "past_due"].includes(plan.status);
  return (
    <SuiteLayout credits={plan?.available} title="Billing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-testid="suite-billing">
        <p className="font-mono text-xs tracking-[0.3em] text-cyan-400">BILLING & PORTAL</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Billing</h1>
        {plan?.billing_mode === "simulated" && (
          <p className="mt-4 flex items-start gap-2 text-xs text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-lg px-3 py-2" data-testid="billing-simulated-note">
            <Info size={14} className="shrink-0 mt-0.5" /> Preview test mode: the plan subscription and trial are simulated here because the test payment account only supports one-time checkout. Live billing uses real Stripe subscriptions with card-on-file trials.
          </p>
        )}
        <div className="mt-8 grid lg:grid-cols-[1fr_1.3fr] gap-6">
          <Glass className="p-6" data-testid="billing-tier-card">
            <p className="font-mono text-[11px] tracking-[0.25em] text-purple-300">CURRENT PLAN</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-white" data-testid="billing-tier-name">{plan?.plan?.name || "No plan"}</p>
            <p className="mt-1 text-sm" data-testid="billing-tier-status">
              <span className={plan?.status === "past_due" ? "text-amber-300" : ["canceled", "none", undefined].includes(plan?.status) ? "text-slate-400" : "text-emerald-300"}>{STATUS[plan?.status || "none"]}</span>
              {plan?.plan && <span className="text-slate-400"> · ${plan.plan.price}/mo</span>}
            </p>
            <p className="mt-3 text-xs text-slate-400" data-testid="billing-renewal-line">
              {plan?.status === "trialing" && !plan.cancel_at_period_end && `Plan starts in ${daysLeft(plan.trial_ends_at)} day(s), or as soon as all 10 trial credits are used.`}
              {plan?.status === "trialing" && plan.cancel_at_period_end && `Trial cancelled. It ends ${new Date(plan.trial_ends_at).toLocaleDateString()} (or when your 10 trial credits run out) and you won't be charged.`}
              {plan?.status === "active" && plan.period_end && `${plan.cancel_at_period_end ? "Ends" : "Renews"} ${new Date(plan.period_end).toLocaleDateString()}${plan.pending_tier ? ` as ${{ hobbyist: "Hobbyist", creator: "Creator", pro: "Pro Suite" }[plan.pending_tier]}` : ""}.`}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {has && plan.billing_mode === "stripe" && (
                <GhostButton onClick={() => act("portal", Plans.portal)} disabled={!!busy} data-testid="billing-portal-btn">
                  {busy === "portal" ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />} Manage in Stripe portal
                </GhostButton>
              )}
              {has && !plan.cancel_at_period_end && (
                <GhostButton onClick={() => setAskCancel(true)} disabled={!!busy} className="hover:border-rose-400/60" data-testid="billing-cancel-btn">
                  <XCircle size={15} /> Cancel Plan
                </GhostButton>
              )}
              {has && plan.cancel_at_period_end && (
                <GhostButton onClick={() => act("resume", Plans.resume, "Plan resumed")} disabled={!!busy} data-testid="billing-resume-btn"><RotateCcw size={15} /> Keep my plan</GhostButton>
              )}
            </div>
          </Glass>
          <CreditsHud plan={plan} />
        </div>
        <div className="mt-6"><AddonBanner /></div>
        {plan && ["trialing", "active"].includes(plan.status) && !plan.cancel_at_period_end && (
          <div className="mt-6"><PlanChanger plan={plan} onChange={set} /></div>
        )}
        {plan && !["trialing", "active"].includes(plan.status) && (
          <div className="mt-12"><h2 className="mb-8 font-display text-2xl font-bold text-white">Choose your plan</h2><PricingCards trialEligible={plan.trial_eligible} /></div>
        )}
      </div>
      <SuiteConfirm open={askCancel} title="Cancel your plan?" testid="billing-cancel-dialog"
        body={plan?.status === "trialing" ? "Your trial ends without any charge. You can keep using your remaining trial credits until then." : "Your plan stays active, with its credits, until the end of the period you've paid for."}
        confirmLabel="Cancel Plan" onCancel={() => setAskCancel(false)}
        onConfirm={() => { setAskCancel(false); act("cancel", Plans.cancel, "Plan cancelled"); }} />
    </SuiteLayout>
  );
}
